import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'

import { Drawer } from '@loadsmart/miranda-react'

import { RequestStatus } from 'core/domain/Request'
import { isPast } from 'core/utils/date'

import {
  type AppointmentFields,
  type Carrier,
  type ScheduleFormValues,
} from '../../domain/Shipment'
import { useGetOpenDockCarrierId } from '../hooks/useGetOpenDockCarrierId'
import { useGetOpenDockWarehouseDetails } from '../hooks/useGetOpenDockWarehouseDetails'
import { CreateOpenDockAccount } from './CreateOpenDockAccount'
import { DrawerLoadingBody, DrawerShell } from './DrawerShell'
import { DrawerStep } from './DrawerStep'
import { OpenDockDrawerProvider, type OpenDockDrawerValue } from './OpenDockDrawerContext'
import { INITIAL_STEP, type Step } from './steps'

export function OpenDockAppointmentDrawer({
  isOpen,
  onClose,
  shipmentUUID,
  shipmentCarrier,
  stopIndex,
  facilityTimezone,
  onConfirm,
  stopDate,
}: {
  isOpen: boolean
  onClose: () => void
  shipmentUUID: string
  shipmentCarrier: Carrier
  stopIndex: number
  facilityTimezone?: string
  onConfirm: () => void
  stopDate: string
}) {
  const [step, setStep] = useState<Step>(INITIAL_STEP)
  const [createdCarrierId, setCreatedCarrierId] = useState<string>()

  const [fields, setFields] = useState<AppointmentFields>({
    staticFields: {
      loadType: null,
      dock: null,
      appointmentDate: isPast(new Date(stopDate)) ? null : `${stopDate}T12:00:00.000Z`, // Using the same time and timezone as selected in the datepicker
      appointmentTime: null,
    },
    dynamicFields: {},
  })

  function setStaticFields(staticFields: Partial<ScheduleFormValues>) {
    setFields(prev => ({
      ...prev,
      staticFields: {
        ...prev.staticFields,
        ...staticFields,
      },
    }))
  }

  const { openDockCarrierId, status: carrierStatus } = useGetOpenDockCarrierId(shipmentUUID, isOpen)

  const carrierId = openDockCarrierId ?? createdCarrierId
  const hasCarrierAccount = Boolean(carrierId)
  const isVerifyingCarrierAccount = !hasCarrierAccount && carrierStatus !== RequestStatus.Done

  const { warehouseDetails, status: warehouseStatus } = useGetOpenDockWarehouseDetails(
    shipmentUUID,
    stopIndex,
    isOpen && hasCarrierAccount
  )

  const loadErrorMessage = getLoadErrorMessage({ carrierStatus, warehouseStatus })

  useEffect(() => {
    if (loadErrorMessage) {
      toast.error(loadErrorMessage)
      onClose()
    }
  }, [loadErrorMessage, onClose])

  if (loadErrorMessage) {
    return null
  }

  if (isVerifyingCarrierAccount) {
    return (
      <DrawerShell isOpen={isOpen} onClose={onClose}>
        <DrawerLoadingBody label="Verifying your Opendock account" />
        <Drawer.Actions>
          <Drawer.ActionPrimary disabled loading>
            Create account and continue
          </Drawer.ActionPrimary>
        </Drawer.Actions>
      </DrawerShell>
    )
  }

  if (!carrierId) {
    return (
      <DrawerShell isOpen={isOpen} onClose={onClose}>
        <CreateOpenDockAccount
          shipmentUUID={shipmentUUID}
          shipmentCarrier={shipmentCarrier}
          onCarrierCreated={setCreatedCarrierId}
        />
      </DrawerShell>
    )
  }

  if (!warehouseDetails) {
    return (
      <DrawerShell isOpen={isOpen} onClose={onClose}>
        <DrawerLoadingBody />
        <Drawer.Actions>
          <Drawer.ActionTertiary disabled>Enter Confirmation Number</Drawer.ActionTertiary>
          <Drawer.ActionPrimary disabled>Start</Drawer.ActionPrimary>
        </Drawer.Actions>
      </DrawerShell>
    )
  }

  const drawerValue: OpenDockDrawerValue = {
    shipmentUUID,
    stopIndex,
    facilityTimezone,
    shipmentCarrier,
    warehouseDetails,
    carrierId,
    fields,
    setStep,
    setStaticFields,
    onConfirm,
    onClose,
  }

  return (
    <OpenDockDrawerProvider value={drawerValue}>
      <DrawerShell isOpen={isOpen} onClose={onClose}>
        <DrawerStep step={step} />
      </DrawerShell>
    </OpenDockDrawerProvider>
  )
}

function getLoadErrorMessage({
  carrierStatus,
  warehouseStatus,
}: {
  carrierStatus: RequestStatus
  warehouseStatus: RequestStatus
}) {
  if (carrierStatus === RequestStatus.Error) {
    return 'Failed to verify your Opendock account. Please try again later.'
  }

  if (warehouseStatus === RequestStatus.Error) {
    return 'Failed to load warehouse details. Please try again later.'
  }

  return null
}
